import type {WebGl2Context} from "#webgl2/webgl2-context"
import type {WebGl2ShaderRef} from "#webgl2/webgl2-shader"
import {WebGl2Shader} from "#webgl2/webgl2-shader"
import type {ShaderReturnType} from "@cm/hal"
import {HalRef} from "@cm/hal"

const KEEP_SHADERS_INDEFINITELY = true // if this is true the shader will not be release when the last reference is released, allowing for the same shader code to be reused without recompilation

export class WebGl2ShaderCache {
    constructor(private context: WebGl2Context) {}

    dispose(): void {
        for (const shaderRef of this.shaderBySource.values()) {
            shaderRef.release()
        }
        this.shaderBySource.clear()
    }

    getOrCreateShader(source: string, returnType?: ShaderReturnType, conservativeRasterization?: boolean): WebGl2ShaderRef {
        let shaderRef = this.shaderBySource.get(source)
        const justCreated = !shaderRef
        if (!shaderRef) {
            shaderRef = new HalRef(new WebGl2Shader(this.context, source, returnType ?? "vec4", conservativeRasterization ?? false), (shader) => {
                this.shaderBySource.delete(source)
                shader.dispose()
            })
            this.shaderBySource.set(source, shaderRef)
        }
        shaderRef.addRef()
        if (!KEEP_SHADERS_INDEFINITELY && justCreated) {
            shaderRef.release() // we don't keep an explicit reference to the shader, so it will be deleted when the last user releases it
        }
        return shaderRef
    }

    get isEmpty(): boolean {
        return this.shaderBySource.size === 0
    }

    private readonly shaderBySource = new Map<string, WebGl2ShaderRef>()
}
